import React from 'react';
import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import TimestampUpdates from './TimestampUpdates';
import ContainerTypeAmountsUpdates from './ContainerTypeAmountsUpdates';
import ContainerStationsUpdates from './ContainerStationsUpdates';
import ContainerStationAmountUpdates from './ContainerStationAmountUpdates';
import AssetsUpdates from './AssetsUpdates';
import { ContainerType } from '../OrderFulfillmentAddContainerTypes';
import { OrderFulfillmentChange } from '../../../../../graphql/generatedTypes';
import RemovedContainerStationManualAmount from './RemovedContainerStationManualAmount';

type ChangeHistoryTableProps = {
  changeHistory: OrderFulfillmentChange[];
  isDeliveryOFType: boolean;
  containerTypes: ContainerType[];
  orderFulfillment: any;
  isInternal: boolean;
};

const ChangeHistoryTable = ({
  changeHistory,
  isDeliveryOFType,
  containerTypes,
  orderFulfillment,
  isInternal,
}: ChangeHistoryTableProps) => {
  return (
    <Table striped>
      <tbody>
        {[...changeHistory]
          .sort((a, b) => b.changed_at.localeCompare(a.changed_at))
          .map((change) => {
            const parsedOldValues = JSON.parse(change.old_values);
            const parsedNewValues = JSON.parse(change.new_values);

            const renderTimestampChanges =
              parsedOldValues?.Timestamp?.length > 0;

            const sortedOldContainerTypeAmounts = [
              ...(parsedOldValues?.ContainerTypeAmounts || []),
            ].sort((a: any, b: any) => a.ContainerTypeId - b.ContainerTypeId);
            const sortedNewContainerTypeAmounts = [
              ...(parsedNewValues?.ContainerTypeAmounts || []),
            ].sort((a: any, b: any) => a.ContainerTypeId - b.ContainerTypeId);

            const containerStationIdsOld = parsedOldValues?.ContainerStationIds;
            const containerStationIdsNew = parsedNewValues?.ContainerStationIds;

            const containerStationAmountsOld =
              parsedOldValues?.UpdatedContainerStationInOf;
            const containerStationAmountsNew =
              parsedNewValues?.UpdatedContainerStationInOf;

            const removedContainerStationManualAmount =
              parsedOldValues?.RemovedContainerStationManualAmountInOf;

            const assetIdsOld = parsedOldValues?.AssetIds;
            const assetIdsNew = parsedNewValues?.AssetIds;

            return (
              <tr key={`${change.changed_at}${change.old_values}`}>
                <td>
                  {new Date(change.changed_at).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true),
                  )}
                </td>
                <td>
                  {isInternal ? (
                    <Link
                      to={'/users/' + change.changed_by_id}
                      className="form-control border-0"
                    >
                      {change.changed_by_name}
                    </Link>
                  ) : (
                    change.changed_by_name
                  )}
                </td>
                <td>
                  {renderTimestampChanges &&
                    !sortedOldContainerTypeAmounts &&
                    !sortedNewContainerTypeAmounts && (
                      <TimestampUpdates
                        oldTimestamp={parsedOldValues}
                        newTimestamp={parsedNewValues}
                      />
                    )}
                  {!renderTimestampChanges &&
                    sortedOldContainerTypeAmounts &&
                    sortedNewContainerTypeAmounts && (
                      <ContainerTypeAmountsUpdates
                        oldAmounts={sortedOldContainerTypeAmounts}
                        newAmounts={sortedNewContainerTypeAmounts}
                        isDeliveryOFType={isDeliveryOFType}
                        containerTypes={containerTypes}
                      />
                    )}

                  {containerStationIdsOld && containerStationIdsNew && (
                    <ContainerStationsUpdates
                      oldStationIds={containerStationIdsOld}
                      newStationIds={containerStationIdsNew}
                    />
                  )}

                  {containerStationAmountsOld && containerStationAmountsNew && (
                    <ContainerStationAmountUpdates
                      oldAmounts={containerStationAmountsOld}
                      newAmounts={containerStationAmountsNew}
                      containerTypes={containerTypes}
                      containerStations={
                        orderFulfillment?.container_stations || []
                      }
                      isInternal={isInternal}
                    />
                  )}

                  {assetIdsOld && assetIdsNew && (
                    <AssetsUpdates
                      oldAssetIds={assetIdsOld}
                      newAssetIds={assetIdsNew}
                    />
                  )}

                  {removedContainerStationManualAmount && (
                    <RemovedContainerStationManualAmount
                      containerTypes={containerTypes}
                      removedManualAmount={removedContainerStationManualAmount}
                    />
                  )}
                </td>
              </tr>
            );
          })}
      </tbody>

      <thead>
        {/* This is below tbody on purpose! To have the <th> sticky above the tbody. */}
        <tr>
          <th className="sticky-table-header">
            {i18n.t('OrderFulfillment:changedAt')}
          </th>
          <th className="sticky-table-header">
            {i18n.t('OrderFulfillment:changedBy')}
          </th>
          <th className="sticky-table-header">
            {i18n.t('OrderFulfillment:updates')}
          </th>
        </tr>
      </thead>
    </Table>
  );
};

export default ChangeHistoryTable;
